import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Request } from "../../request/Request";

export interface IInterfacePopup {
  isOpen: boolean;
}

export interface IPaymentPopup {
  isOpen: boolean;
  isPaywall?: boolean;
}

export interface ILoginPopupError {
  isOpen: boolean;
  errorText?: string;
  urlParams?: Record<string, any>;
  onHide?: () => void;
  uiOptions?: {
    phoneFirst?: boolean;
    fullScreen?: boolean;
    noCancel?: boolean;
  };
}

export interface IStickyPlayer {
  isOpen: boolean;
}

export interface ICheckoutBox {
  isOpen: boolean;
  height?: number;
}

export interface IReviewComplainPopup {
  id: number;
  isOpen: boolean;
}

export interface IForceScroll {
  scrollToReviewForm: boolean;
  scrollToReviewId: number;
}

export interface ISpamTickes {
  isOpen: boolean;
  isHtmlFetched?: boolean; // Временный костыль до появления новых notification
}

export interface ISharingPopup {
  reviewId: number;
  userName: string;
  rating: number;
  comment: string;
  date: string;
  hasAvatar: boolean;
  userId: number;
  isReply: boolean;
  isOpen: boolean;
}

export interface IInterfaceState {
  loginPopupError?: ILoginPopupError;
  stickyPlayer: IStickyPlayer;
  isPageWithPlayer: boolean;
  checkoutBox: ICheckoutBox;
  helpdesk?: IInterfacePopup;
  spamTickets?: ISpamTickes;
  paymentPopup?: IPaymentPopup;
  reviewComplainPopup: IReviewComplainPopup;
  forceScroll: IForceScroll;
  sharingPopup: ISharingPopup;
  headerHeight: number;
}

const initialState: IInterfaceState = {
  loginPopupError: {
    isOpen: false,
  },
  stickyPlayer: {
    isOpen: false,
  },
  isPageWithPlayer: true,
  checkoutBox: {
    isOpen: false,
    height: 0,
  },
  helpdesk: {
    isOpen: false,
  },
  spamTickets: {
    isOpen: false,
    isHtmlFetched: false,
  },
  paymentPopup: {
    isOpen: false,
  },
  reviewComplainPopup: {
    isOpen: false,
    id: 0,
  },
  forceScroll: {
    scrollToReviewForm: false,
    scrollToReviewId: 0,
  },
  sharingPopup: {
    reviewId: 0,
    userName: "",
    userId: 0,
    rating: 0,
    comment: "",
    date: "",
    hasAvatar: false,
    isOpen: false,
    isReply: false,
  },
  headerHeight: 0,
};
interface ISendClickTrackRequest {
  p: string;
  w: number;
  u: string;
  x: string;
  gu_ajax: true;
}
export const sendClickTrack = createAsyncThunk(
  "interface/sendClickTrackGUJ",
  async (params: ISendClickTrackRequest, { requestId }) => {
    const response = new Request.GUJ<
      ISendClickTrackRequest,
      Record<string, any>
    >({
      url: "/clctrack/",
      provider: {
        id: requestId,
      },
      request: {
        method: "GET",
        params: { ...params },
      },
    });

    return await response.fetch();
  }
);

export const interfaceSlice = createSlice({
  name: "global/interface",
  initialState,
  reducers: {
    setLoginPopupError: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["loginPopupError"]>
    ) => {
      state.loginPopupError = action.payload;
    },
    setStickyPlayer: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["stickyPlayer"]>
    ) => {
      state.stickyPlayer = action.payload;
    },
    setCheckoutBox: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["checkoutBox"]>
    ) => {
      state.checkoutBox = action.payload;
    },
    setHelpdeskConfig: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["helpdesk"]>
    ) => {
      state.helpdesk = action.payload;
    },
    setSpamTicketsConfig: (
      state: IInterfaceState,
      action: PayloadAction<ISpamTickes>
    ) => {
      state.spamTickets = action.payload;
    },
    setPaymentPopup: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["paymentPopup"]>
    ) => {
      state.paymentPopup = action.payload;
    },
    setReviewComplainPopup: (
      state: IInterfaceState,
      action: PayloadAction<IInterfaceState["reviewComplainPopup"]>
    ) => {
      state.reviewComplainPopup = action.payload;
    },
    setForceScrollToReviewForm: (
      state: IInterfaceState,
      action: PayloadAction<IForceScroll>
    ) => {
      state.forceScroll = action.payload;
    },
    setSharingPopup: (
      state: IInterfaceState,
      action: PayloadAction<ISharingPopup>
    ) => {
      state.sharingPopup = action.payload;
    },
    setHeaderHeight: (
      state: IInterfaceState,
      action: PayloadAction<number>
    ) => {
      state.headerHeight = action.payload;
    },
    setIsPageWithPlayer: (
      state: IInterfaceState,
      action: PayloadAction<boolean>
    ) => {
      state.isPageWithPlayer = action.payload;
    },
  },
});

export const {
  setLoginPopupError,
  setStickyPlayer,
  setCheckoutBox,
  setHelpdeskConfig,
  setSpamTicketsConfig,
  setPaymentPopup,
  setReviewComplainPopup,
  setForceScrollToReviewForm,
  setSharingPopup,
  setHeaderHeight,
  setIsPageWithPlayer,
} = interfaceSlice.actions;

export default interfaceSlice.reducer;
